import {
  ArrowTrendingUpIcon,
  BookOpenIcon,
  CalendarIcon,
  ChartBarSquareIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  GiftIcon,
  WrenchScrewdriverIcon,
  IdentificationIcon,
  MegaphoneIcon,
  NewspaperIcon,
  PencilSquareIcon,
  ShoppingBagIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';

import { ClientOnly } from '@/components/ClientOnly';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Logo } from '@/components/Logo';

import UserDropdown from '@/components/Nav/UserDropdown';
import useMe from './utils/useMe';
import { usePageContext } from './utils/usePageContext';
import { useRasContext } from './utils/useRasContext';

import NavLink from '@/components/Nav/NavLink';
import type { Link } from '@/components/Nav/NavLink';

export default Nav;

const HIDE_ON = [
  '/',
  '/signin',
  '/register',
  '/forgot-password',
  '/links',
  '/mailing-list',
  '/who-is-ras-for',
];

const LINKS: Link[] = [
  {
    count: { dataPath: '/release-center', dataName: 'messageReceipt/created' },
    href: '/release-center',
    icon: MegaphoneIcon,
    text: 'Release Center',
  },
  {
    href: '/results',
    startsWithHref: '/results',
    icon: ArrowTrendingUpIcon,
    text: 'My Results',
    roleRequired: true,
  },
  {
    href: '/archive',
    startsWithHref: '/archive',
    icon: ChartBarSquareIcon,
    text: 'All Results',
  },
  {
    count: {
      dataPath: '/support-chat',
      dataName: 'directMessageReceipt/created',
      dataCb: (_event, json, setCount, map, me) => {
        if (me?.role === 'admin' || me?.role === 'staff') {
          // I'm an admin receiving a DM notification
          if (
            (json.data.user?.role === 'admin' || json.data.user?.role === 'staff') &&
            json.data.receiverUserId
          ) {
            // When an admin messages a user, we clear all the notifications for this chat for everyone

            const path = `/support-chat/${json.data.receiverUserId}`;
            const pathCount = map.current.get(path);

            if (pathCount) {
              setCount((count) => Math.max(count - pathCount, 0));
              map.current.delete(path);
            }
          } else {
            // When a regular user messages admins, we increment the count if we aren't on that page already
            const path = `/support-chat/${json.data.userId}`;
            const pathCount = map.current.get(path);

            // 1647 is Cleatus
            if (!pathCount && location.pathname !== path && path !== '/support-chat/1647') {
              map.current.set(path, 1);
              setCount((count) => count + 1);
            }
          }
        } else {
          // I'm a regular user receiving a DM
          const path = '/support-chat';
          const pathCount = map.current.get(path);

          if (json.data.userId === me?.id) {
            // If I got it from myself, set unread to 0
            map.current.delete(path);
            setCount(0);
          } else {
            // Otherwise
            map.current.set(path, (pathCount || 0) + 1);
            setCount((count) => count + 1);
          }
        }
      },
      dataNavCb(path, setCount, map) {
        const pathCount = map.current.get(path);

        if (pathCount) {
          setCount((count) => Math.max(count - pathCount, 0));
          map.current.delete(path);
        }
      },
    },
    href: '/support-chat',
    startsWithHref: '/support-chat',
    icon: ChatBubbleLeftRightIcon,
    text: 'Support Chat',
  } as Link<API.DirectMessageReceipt>,
  {
    href: '/order',
    startsWithHref: '/order',
    icon: ShoppingBagIcon,

    text: 'Order',
  },
  {
    href: '/release-schedule',
    icon: CalendarIcon,
    text: 'Release Schedule',
  },
  {
    href: '/welcome-guide',
    icon: BookOpenIcon,
    text: 'Welcome Guide',
  },
  {
    href: '/walls',
    startsWithHref: '/walls',
    icon: NewspaperIcon,
    text: "Walls' Blog",
  },
  {
    href: '/about',
    icon: IdentificationIcon,
    text: 'About RAS',
  },
];

type TempLink = {
  href: string;
  text: string;
};

const TEMPLINKS: TempLink[] = import.meta.env.DEV
  ? [
      {
        href: '/signin',
        text: 'Sign in',
      },
      {
        href: '/register',
        text: 'Sign Up',
      },
      {
        href: '/forgot-password',
        text: 'Forgot Password',
      },
      {
        href: '/404',
        text: '404',
      },
      {
        href: '/',
        text: 'Index',
      },
    ]
  : [];

function Nav({ withoutUser = false }) {
  const { urlParsed, urlPathname } = usePageContext();
  const ref = useRef<HTMLInputElement>(null);

  const rasContext = useRasContext();
  // biome-ignore lint/correctness/useHookAtTopLevel: This is used in the <Suspense> of Nav, so it never changes
  const me = withoutUser ? null : useMe();
  const user = me || rasContext.data?.user;

  const filteredLinks = LINKS.filter((link) => {
    if (link.roleRequired && !user?.role) {
      return false;
    }

    return true;
  });

  if (HIDE_ON.includes(urlPathname) || urlPathname.startsWith('/order')) {
    return null;
  }

  return (
    <>
      <div className="fixed inset-0 z-20 -translate-x-full bg-zinc-400/20 backdrop-blur-xs transition-transform peer-checked/nav-checkbox:-translate-x-0 peer-focus-within/nav:-translate-x-0 dark:bg-black/40" />
      <input
        ref={ref}
        id="nav-checkbox"
        type="checkbox"
        className="peer/nav-checkbox absolute z-[-1] m-0 size-10 border-collapse cursor-pointer border-none border-transparent bg-transparent p-0 opacity-0 md:hidden"
        defaultChecked={false}
      />
      <aside
        id="ras-sidebar"
        aria-label="Sidebar"
        className="peer/nav fixed top-0 left-0 z-40 flex h-[100dvh] w-[100dvw] flex-none -translate-x-full flex-col bg-zinc-900 text-sm text-white transition-transform select-none peer-checked/nav-checkbox:translate-x-0 md:w-72 md:translate-x-0 dark:border-r dark:border-white/10 dark:bg-zinc-950"
        onClick={(event) => {
          const target = event.target as HTMLAnchorElement;

          if (target?.closest('a[href]') && ref.current) {
            // Immediately close menu if anchor clicked
            target.blur();
            ref.current.focus();
            ref.current.click();
          }
        }}
      >
        <article className="flex grow flex-col space-y-5 overflow-y-auto overscroll-contain p-4 px-6">
          <div className="flex items-center justify-between">
            <Logo className="size-10 select-none" />
            <div className="flex flex-row items-center">
              <a href="/settings">
                <Cog6ToothIcon
                  height={24}
                  className="hidden size-6 shrink-0 text-zinc-400 hover:text-white active:text-white md:block"
                  aria-hidden="true"
                />
              </a>
              <label
                htmlFor="nav-checkbox"
                className="ml-2 text-zinc-400 peer-checked/nav-checkbox:inline-block peer-focus-within/nav:inline-block hover:text-white md:pointer-events-none md:hidden md:opacity-0"
              >
                <XMarkIcon height={24} className="size-6 shrink-0" aria-hidden="true" />
              </label>
            </div>
          </div>
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col space-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  {filteredLinks.map((link) => {
                    return (
                      <NavLink key={link.href + link.text} link={link} urlParsed={urlParsed} />
                    );
                  })}

                  {/* Editor entry — Walls' blog CMS, only for authors/admins */}
                  {(me?.role === 'author' || me?.role === 'admin') && (
                    <li>
                      <a
                        href="/editor"
                        data-active={urlParsed.pathnameOriginal.startsWith('/editor')}
                        className="flex w-full items-center gap-x-3 rounded px-3 py-2 leading-6 font-semibold text-emerald-300 transition-[background-color,color] duration-150 hover:bg-emerald-400/15 hover:text-emerald-200 active:bg-emerald-400/20 data-[active=true]:bg-emerald-400/20 data-[active=true]:text-emerald-200 data-[active=true]:ring-1 data-[active=true]:ring-emerald-400/30 data-[active=true]:ring-inset"
                      >
                        <PencilSquareIcon className="size-6 shrink-0" aria-hidden="true" />
                        <span className="flex-1 whitespace-nowrap">Editor</span>
                      </a>
                    </li>
                  )}

                  {/* Admin entry — only visible to admin/staff */}
                  {(me?.role === 'admin' || me?.role === 'staff') && (
                    <li>
                      <a
                        href="/admin"
                        data-active={urlParsed.pathnameOriginal.startsWith('/admin')}
                        className="flex w-full items-center gap-x-3 rounded px-3 py-2 leading-6 font-semibold text-amber-300 transition-[background-color,color] duration-150 hover:bg-amber-400/15 hover:text-amber-200 active:bg-amber-400/20 data-[active=true]:bg-amber-400/20 data-[active=true]:text-amber-200 data-[active=true]:ring-1 data-[active=true]:ring-amber-400/30 data-[active=true]:ring-inset"
                      >
                        <WrenchScrewdriverIcon className="size-6 shrink-0" aria-hidden="true" />
                        <span className="flex-1 whitespace-nowrap">Admin</span>
                      </a>
                    </li>
                  )}
                </ul>
              </li>
              {/* Temporary Links (dev only) */}
              {TEMPLINKS.length > 0 && (
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {TEMPLINKS.map((link) => {
                      return (
                        <li key={link.href + link.text}>
                          <a
                            href={link.href}
                            data-active={urlParsed.pathnameOriginal === link.href}
                            className="flex w-full items-center rounded-sm p-2 text-zinc-400 transition-[width,color,background-color] duration-500 ease-out hover:bg-white/5 hover:text-white hover:duration-75 active:bg-white/5 active:text-white data-[active=true]:bg-white/10 data-[active=true]:text-white"
                          >
                            <span className="ml-3 flex-1 whitespace-nowrap">{link.text}</span>
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              )}
              <li>
                <ErrorBoundary>
                  <ClientOnly component={() => import('@/components/Nav/ActiveServices')} />
                </ErrorBoundary>
              </li>
            </ul>
          </nav>
        </article>
        <article className="mt-auto flex grow-0 flex-col space-y-1 p-2">
          <RotatingPromo urlParsed={urlParsed} />
          <hr className="h-px border-0 bg-white/10" />
          <div className="flex grow justify-between gap-x-1.5">
            <ErrorBoundary>
              <ClientOnly
                component={() => import('@/components/Nav/UserDropdown')}
                key="client-user"
              >
                <UserDropdown withoutUser={true} user={user} key="server-user" />
              </ClientOnly>
              <ClientOnly component={() => import('@/components/UI/DesktopNotifications')} />
            </ErrorBoundary>
          </div>
        </article>
      </aside>
    </>
  );
}

// ── Rotating sportsbook promo ────────────────────────────────────────────────

const PROMOS: { href: string; text: string; color: 'red' | 'secondary' | 'primary' }[] = [
  { href: '/betonline', text: 'Get $250 credit with BetOnline', color: 'red' },
  { href: '/bet105', text: 'Get $250 credit with bet105', color: 'secondary' },
  { href: '/oddslogic', text: 'Get $250 credit with OddsLogic', color: 'primary' },
];

const ROTATE_MS = 6000;

function RotatingPromo({
  urlParsed,
}: {
  urlParsed: ReturnType<typeof usePageContext>['urlParsed'];
}) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % PROMOS.length);
        setVisible(true);
      }, 300); // matches the CSS transition duration
    }, ROTATE_MS);

    return () => clearInterval(interval);
  }, []);

  const promo = PROMOS[index];

  return (
    <div
      style={{
        opacity: visible ? 1 : 0,
        transition: 'opacity 300ms ease',
      }}
    >
      <NavLink
        link={{ href: promo.href, icon: GiftIcon, text: promo.text }}
        color={promo.color}
        urlParsed={urlParsed}
      />
    </div>
  );
}
